import React, { useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { MarketplaceIds } from '../../../enums/marketplace-ids';
import { useModal } from '../../../hooks/use-modal';
import { OrderSourceDeactivationModal } from '../../../pages/settings/stores/order-source-deactivation-modal/order-source-deactivation-modal';
import {
  UNLIMITED_PLAN_LIMIT,
  useOrderSourceLimitForCurrentBillingPlan,
} from '../../../pages/settings/payment-and-subscription/hooks/use-plan-details';
import Global from '../../../store/global-selectors';

/**
 * @returns {React.JSX.Element | null}
 */
export const OrderSourceLimitEnforcer = () => {
  const { close, isOpen, open } = useModal();
  const orderSourceLimit = useOrderSourceLimitForCurrentBillingPlan();
  const orderSources = useSelector(Global.selectSellerStores);

  const externalOrderSources = useMemo(() => {
    return orderSources.filter((store) => store.marketplaceId !== MarketplaceIds.ShipStation);
  }, [orderSources]);

  const isOrderSourceLimitExceeded = useMemo(() => {
    if (orderSourceLimit === UNLIMITED_PLAN_LIMIT) {
      return false;
    }

    const activeExternalOrderSources = externalOrderSources.filter((store) => store.isActive);

    return activeExternalOrderSources.length > orderSourceLimit;
  }, [externalOrderSources, orderSourceLimit]);

  useEffect(() => {
    // If the order source limit is ever exceeded, open the deactivation modal
    if (isOrderSourceLimitExceeded && !isOpen) {
      open();
    }
  }, [isOpen, open, isOrderSourceLimitExceeded, orderSourceLimit]);

  useEffect(() => {
    // If seller upgrades plan to have unlimited order sources, we can automatically close the modal
    if (orderSourceLimit === UNLIMITED_PLAN_LIMIT && isOpen) {
      close();
    }
  }, [close, orderSourceLimit, isOpen]);

  return (
    isOpen && (
      <OrderSourceDeactivationModal
        isOrderSourceLimitExceeded={isOrderSourceLimitExceeded}
        maxAllowedOrderSources={orderSourceLimit}
        onContinue={close}
        orderSources={externalOrderSources}
      />
    )
  );
};
